/**
 * A lista de sonhos (`bucket_list`) do Flutter: `bucket_item.dart`.
 *
 * O que mora aqui é o que a tela e a prévia do painel precisam dizer igual: os
 * três estados do `BucketStatus`, o formato do documento e a tabela de
 * categorias do `BucketCategory`. As três coisas estavam copiadas dentro de
 * `telas/Desejos.tsx` no site e no celular; a prévia seria a terceira cópia, e
 * três cópias de uma tabela é como uma delas fica para trás.
 *
 * Fica de fora o que é mesmo de cada aparelho: as cores dos selos (o site usa
 * `var(--ouro)`, o celular `cor.ouro`) e as palavras dos filtros, que só a tela
 * inteira mostra.
 *
 * Os nomes das chaves são os do enum do Flutter e são gravados crus: um
 * "Desejo" em português no campo `status` seria um item que o outro app não
 * sabe ler.
 */

import type { NomeDoIcone } from '../theme/desenhos';

/** Os três valores do `BucketStatus`. */
export type EstadoDoDesejo = 'wish' | 'planned' | 'completed';

/**
 * O documento cru, como o Flutter o grava.
 *
 * `status` é `string`, e não `EstadoDoDesejo`, de propósito: o campo vem do
 * banco, que quatro apps escrevem, e prometer os três valores ao TypeScript foi
 * o que deixou `SELOS[status]` passar na compilação e quebrar na tela. Quem
 * precisa do estado passa por `estadoDoDesejo`.
 */
export interface DocDesejo {
  title?: string;
  description?: string;
  category?: string;
  status?: string;
  createdBy?: string;
  createdAt?: string;
  completedAt?: string;
  completedBy?: string;
  imageUrl?: string;
  notes?: string;
}

/**
 * Cada categoria com o seu desenho, na ordem do `BucketCategory`. Lá são
 * emojis; aqui são os desenhos da casa, que é a regra desta parte do app — o
 * que o cartão preserva é o lugar, o tamanho e a cor deles, não o traço do
 * emoji.
 */
export const CATEGORIAS_DE_DESEJO: ReadonlyArray<{
  chave: string;
  nome: string;
  icone: NomeDoIcone;
}> = [
  { chave: 'travel', nome: 'Viagem', icone: 'aviao' },
  { chave: 'experience', nome: 'Experiência', icone: 'teatro' },
  { chave: 'food', nome: 'Comida e bebida', icone: 'comida' },
  { chave: 'adventure', nome: 'Aventura', icone: 'montanha' },
  { chave: 'milestone', nome: 'Conquista', icone: 'marco' },
  { chave: 'other', nome: 'Outros', icone: 'estrela' },
];

const PELA_CHAVE = new Map(CATEGORIAS_DE_DESEJO.map((c) => [c.chave, c]));

/** A última é `other`, que é para onde cai o que o app não conhece. */
const OUTROS = CATEGORIAS_DE_DESEJO[CATEGORIAS_DE_DESEJO.length - 1];

/** A categoria de um item, com `Outros` para quem não trouxe nenhuma. */
export function categoriaDoDesejo(chave: string | undefined) {
  return (chave ? PELA_CHAVE.get(chave) : undefined) ?? OUTROS;
}

const ESTADOS_CONHECIDOS = new Set<string>(['wish', 'planned', 'completed']);

/**
 * O estado de um item, com `wish` para o que o app não conhece.
 *
 * É o `orElse: () => BucketStatus.wish` do `BucketItem.fromMap`, que a porta
 * para cá tinha deixado no caminho: a categoria ganhou o seu `orElse` logo
 * acima, o estado não, e o `SELOS[status]` das duas telas lia um `undefined` e
 * derrubava o app inteiro -- sem casca de erro em volta, todas as telas ficavam
 * em branco por causa de um documento.
 */
export function estadoDoDesejo(chave: string | undefined): EstadoDoDesejo {
  return chave && ESTADOS_CONHECIDOS.has(chave) ? (chave as EstadoDoDesejo) : 'wish';
}
